/**
 * @file CustomerBlendOrganiser.js
 * @version 2.1.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-24
 * @modified 2026-09-29
 * @description Put blend formulations into the right customer blend.
 *
 *              The backfill placed 615 of 674 automatically, from invoice
 *              history plus a type token in the name. The rest are here, and
 *              only someone who knows the accounts can settle them:
 *
 *                Unassigned      sold to more than one customer, or never
 *                                invoiced, so no customer could be derived.
 *                Catch-all       a customer running Dairy and Beef also has
 *                                untyped names like "CF Nov 25 Blend", which
 *                                collected into a "Blend" line of their own.
 *
 *              v2 makes the destination a customer plus a line name rather than
 *              an existing line, so a farm with no blends is a valid answer —
 *              previously it was unreachable, which is one of the cases this
 *              screen exists to fix. Rows tick, and one destination applies to
 *              everything ticked: a farm that switched supplier mid-year has
 *              eight generations to move, and eight identical dropdowns is a
 *              worse tool than one.
 *
 *              Renaming lines moved out to the table itself (pencil on the
 *              group header, or right-click it), where you are already looking
 *              when you notice the name is wrong.
 *
 *              The footer commits. Each row's arrow still files that row on its
 *              own, but a half-filled row used to be a silent dead end: the
 *              arrow disables itself when the line name is blank, so someone who
 *              picked the customer and moved on lost the choice on close with
 *              nothing said. Save changes files every complete row, and stops on
 *              the incomplete ones — with the offer to file them all under
 *              "Blend", which is the answer often enough to be worth one click
 *              and is exactly what the catch-all section below then exists to
 *              tidy up.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Input from './Input';
import TickCheckbox from './TickCheckbox';
import ComboBox from './ComboBox';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';
import ItemStatusBadge from './ItemStatusBadge';
import EntityLink from './EntityLink';
import CustomerBlendPicker, { useBlendAccounts } from './CustomerBlendPicker';
import CustomerProductsModal from './CustomerProductsModal';
import useCustomerBlends from '../hooks/useCustomerBlends';
import { useSlideOutButton } from './SlideOutButton';
import SearchHelp from './SearchHelp';
import { matchesAllTerms } from '../utils/searchMatch';
import './CustomerBlendOrganiser.css';
import './CustomerBlendPicker.css';

function OrganiserBody({
  loading, accounts, blends, unassigned, catchAll,
  search, setSearch,
  selected, toggleSelect, toggleSelectAll, allSelected,
  bulkTarget, setBulkTarget, applyBulk,
  rowTarget, setRowTarget, applyRow,
  onViewCustomer, onMerge, mergeTarget, setMergeTarget, needsLine, busy
}) {
  useSlideOutButton([busy, loading, selected.size]);

  if (loading || accounts.loading) return <LoadingSpinner />;

  return (
    <div className="cbo-body">
      <div className="cbo-search">
        <Input type="text" icon="search" value={search}
               onChange={(e) => setSearch(e.target.value)}
               placeholder="Search product, code or customer..." />
        <SearchHelp fields={['product', 'item code', 'customer', 'blend name']} />
      </div>

      <section className="cbo-section">
        <h4>
          Unassigned
          <span className="cbo-count">{unassigned.length}</span>
        </h4>
        <p className="cbo-hint">
          No customer could be derived — sold to several customers, or never
          invoiced. Tick the ones that go to the same farm and set the
          destination once.
        </p>

        {/* The bulk bar is the point of the rewrite, so it sits above the list
            rather than appearing only once something is ticked — otherwise the
            first-time user works row by row and never learns it is there. */}
        <div className={`cbo-bulk${selected.size ? ' active' : ''}`}>
          <TickCheckbox
            id="cbo-select-all"
            checked={allSelected}
            onChange={toggleSelectAll}
            ariaLabel="Select all unassigned"
          />
          <span className="cbo-bulk-count">
            {selected.size ? `${selected.size} selected` : 'Select all'}
          </span>
          <CustomerBlendPicker
            accountOptions={accounts.options}
            accountsById={accounts.byId}
            blends={blends}
            value={bulkTarget}
            onChange={setBulkTarget}
            onViewCustomer={onViewCustomer}
            disabled={busy || !selected.size}
            compact
          />
          <button className="btn-slideout" onClick={applyBulk}
                  disabled={busy || !selected.size || !bulkTarget.accountId
                            || !String(bulkTarget.name || '').trim()}>
            <div className="icon-wrapper"><span className="material-symbols-rounded">move_group</span></div>
            <span className="btn-label">{busy ? 'Moving...' : `Move ${selected.size || ''}`.trim()}</span>
          </button>
        </div>

        {unassigned.length === 0 ? (
          <p className="cbo-empty">Nothing unassigned.</p>
        ) : (
          <ul className="cbo-list">
            {unassigned.map(u => (
              <li key={u.formulation_id}>
                <TickCheckbox
                  id={`cbo-${u.formulation_id}`}
                  checked={selected.has(u.formulation_id)}
                  onChange={() => toggleSelect(u.formulation_id)}
                  ariaLabel={`Select ${u.item_code}`}
                />
                <span className="cbo-item">
                  <EntityLink type="inventory_item" id={u.inventory_item_id}
                              variant="badge" className="account-badge-inline">
                    {u.item_code}
                  </EntityLink>
                  <EntityLink type="inventory_item" id={u.inventory_item_id}
                              className="cbo-item-name" title={u.item_name}>
                    {u.item_name}
                  </EntityLink>
                  <ItemStatusBadge item={u} />
                </span>
                <span className="cbo-move">
                  <CustomerBlendPicker
                    accountOptions={accounts.options}
                    accountsById={accounts.byId}
                    blends={blends}
                    value={rowTarget[u.formulation_id] || {}}
                    onChange={(v) => setRowTarget(prev => ({ ...prev, [u.formulation_id]: v }))}
                    onViewCustomer={onViewCustomer}
                    invalid={needsLine?.has(u.formulation_id)}
                    disabled={busy}
                    compact
                  />
                  <button className="btn-icon" title="Move this one"
                          disabled={busy
                            || !rowTarget[u.formulation_id]?.accountId
                            || !String(rowTarget[u.formulation_id]?.name || '').trim()}
                          onClick={() => applyRow(u.formulation_id)}>
                    <span className="material-symbols-rounded">arrow_forward</span>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Not a backlog. "Blend" is an accepted default name, so a customer
          whose untyped generations sit under it is filed correctly and nothing
          here is waiting on anyone — which is why this section no longer
          carries a count and is not in the button's badge. It stays because it
          is the only place a whole line can be emptied into another in one
          gesture; a customer who turns out to want "Milkers" and "Youngstock"
          is a rename away, not a re-file. */}
      <section className="cbo-section cbo-optional">
        <h4>Tidy blend lines</h4>
        <p className="cbo-hint">
          Optional. These customers have a generic “Blend” line alongside named
          ones. Leave it — “Blend” is a perfectly good name — or merge it into
          another line, typing a new name to create one.
        </p>
        {catchAll.length === 0 ? (
          <p className="cbo-empty">No generic “Blend” lines alongside named ones.</p>
        ) : (
          <ul className="cbo-list">
            {catchAll.map(b => (
              <li key={b.id}>
                <span className="cbo-item">
                  {b.account_no && <span className="account-badge-inline">{b.account_no}</span>}
                  <EntityLink type="contact" id={b.account_id} className="cbo-item-name">
                    {b.company_name}
                  </EntityLink>
                  <span className="fm-blend-line-badge">{b.name}</span>
                  <span className="cbo-gen-count">
                    {b.generations.length} formulation{b.generations.length !== 1 ? 's' : ''}
                  </span>
                </span>
                <span className="cbo-move">
                  {/* A ComboBox, not a dropdown: the answer is often a line the
                      customer does not have yet ("these are Youngstock"), and
                      offering only their existing lines meant leaving the modal
                      to create one — which is the very tidy-up this section is
                      asking for. Picking an existing line sends its id; typing
                      anything else sends the name and the server creates it. */}
                  <ComboBox
                    value={mergeTarget[b.id] || ''}
                    onChange={(v) => setMergeTarget(prev => ({ ...prev, [b.id]: v }))}
                    options={blends
                      .filter(o => o.account_id === b.account_id && o.id !== b.id)
                      .map(o => ({ value: o.name, label: o.name }))}
                    placeholder="Merge into, or type a new line..."
                    disabled={busy}
                  />
                  <button className="btn-icon" title="Merge and remove this line"
                          disabled={!mergeTarget[b.id] || busy}
                          onClick={() => onMerge(b, mergeTarget[b.id])}>
                    <span className="material-symbols-rounded">merge</span>
                  </button>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function CustomerBlendOrganiser({ onClose, onChanged }) {
  const accounts = useBlendAccounts();
  const { blends, unassigned, loading, reload } = useCustomerBlends();
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [bulkTarget, setBulkTarget] = useState({});
  const [rowTarget, setRowTarget] = useState({});
  const [mergeTarget, setMergeTarget] = useState({});
  const [viewing, setViewing] = useState(null);
  // Rows the user was asked to complete. Cleared as each one is filled in, so
  // the marks retreat as the work is done rather than all at once at the end.
  const [needsLine, setNeedsLine] = useState(new Set());
  const [savePrompt, setSavePrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null);

  const matches = useCallback(
    (...parts) => matchesAllTerms(parts, search),
    [search]
  );

  const visibleUnassigned = useMemo(
    () => unassigned.filter(u => matches(u.item_name, u.item_code)),
    [unassigned, matches]
  );

  const sortedBlends = useMemo(
    () => [...blends].sort((a, b) =>
      (a.company_name || '').localeCompare(b.company_name || '') || a.name.localeCompare(b.name)),
    [blends]
  );

  // A "Blend" line only needs attention when the customer has named lines too —
  // a farm whose only line is called "Blend" is not a catch-all, it is a farm
  // with one blend.
  const catchAll = useMemo(
    () => sortedBlends.filter(b =>
      b.name === 'Blend'
      && sortedBlends.some(o => o.account_id === b.account_id && o.id !== b.id)
      && matches(b.company_name, b.name)),
    [sortedBlends, matches]
  );

  const allSelected = visibleUnassigned.length > 0
    && visibleUnassigned.every(u => selected.has(u.formulation_id));

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleSelectAll = () => setSelected(
    allSelected ? new Set() : new Set(visibleUnassigned.map(u => u.formulation_id))
  );

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await reload();
      onChanged?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const post = async (url, body, method = 'PUT') => {
    const res = await fetch(`${API_URL}${url}`, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Request failed');
    return data;
  };

  const place = (formulationId, target) =>
    post(`/api/formulations/customer-blends/place/${formulationId}`,
         { account_id: Number(target.accountId), name: target.name });

  const applyRow = (formulationId) => run(async () => {
    await place(formulationId, rowTarget[formulationId]);
    setRowTarget(prev => { const n = { ...prev }; delete n[formulationId]; return n; });
  });

  // Sequential rather than Promise.all: the first move may create the line the
  // rest are filed into, and racing them would create it several times over —
  // or lose all but one to the unique constraint.
  const applyBulk = () => run(async () => {
    for (const id of selected) await place(id, bulkTarget);
    setSelected(new Set());
    setBulkTarget({});
  });

  /* What the footer will commit. A row counts as started once a customer is
     chosen; it is ready when it also has a line name. Only rows still on screen
     are considered — a target set against a row the search has since filtered
     out must not be filed behind the user's back. */
  const started = useMemo(
    () => visibleUnassigned
      .map(u => ({ id: u.formulation_id, target: rowTarget[u.formulation_id] }))
      .filter(r => r.target?.accountId),
    [visibleUnassigned, rowTarget]
  );
  const ready = useMemo(
    () => started.filter(r => String(r.target.name || '').trim()),
    [started]
  );
  const incomplete = useMemo(
    () => started.filter(r => !String(r.target.name || '').trim()),
    [started]
  );

  // Filling a line in clears its mark straight away.
  useEffect(() => {
    if (!needsLine.size) return;
    const stillEmpty = new Set(incomplete.map(r => r.id));
    if ([...needsLine].every(id => stillEmpty.has(id))) return;
    setNeedsLine(new Set([...needsLine].filter(id => stillEmpty.has(id))));
  }, [incomplete, needsLine]);

  const commit = (rows) => run(async () => {
    // Sequential for the same reason applyBulk is: the first move may create
    // the line the rest are filed into.
    for (const r of rows) await place(r.id, r.target);
    setRowTarget(prev => {
      const next = { ...prev };
      for (const r of rows) delete next[r.id];
      return next;
    });
    setNeedsLine(new Set());
  });

  const handleSave = () => {
    if (incomplete.length === 0) {
      if (ready.length === 0) return onClose();
      return commit(ready).then(() => onClose());
    }
    const n = incomplete.length;
    setSavePrompt({
      message: `${n} row${n === 1 ? ' has' : 's have'} a customer chosen but no blend line.\n\n`
        + `Fill the missing line${n === 1 ? '' : 's'} in, or file ${n === 1 ? 'it' : 'them'} under a line called "Blend" — `
        + `which you can rename or merge later from the catch-all section.`,
      onUseBlend: () => {
        setSavePrompt(null);
        const filled = incomplete.map(r => ({ ...r, target: { ...r.target, name: 'Blend' } }));
        commit([...ready, ...filled]).then(() => onClose());
      },
      onFillIn: () => {
        setSavePrompt(null);
        setNeedsLine(new Set(incomplete.map(r => r.id)));
      }
    });
  };

  const handleMerge = (blend, typed) => {
    const name = String(typed || '').trim();
    if (!name) return;
    // The ComboBox yields a NAME. If the customer already has a line by that
    // name we merge into it by id; otherwise the server creates it. Matched
    // case-insensitively so picking "Milkers" from the list and typing
    // "milkers" reach the same line rather than making a second one.
    const existing = blends.find(
      b => b.account_id === blend.account_id
        && b.id !== blend.id
        && b.name.toLowerCase() === name.toLowerCase()
    );
    const count = blend.generations.length;
    setConfirm({
      message: existing
        ? `Move all ${count} formulation${count !== 1 ? 's' : ''} from “${blend.name}” into “${existing.name}” and remove “${blend.name}”?`
        : `Create a new line “${name}” for ${blend.company_name}, move all ${count} formulation${count !== 1 ? 's' : ''} into it and remove “${blend.name}”?`,
      onConfirm: () => {
        setConfirm(null);
        run(async () => {
          await post(`/api/formulations/customer-blends/${blend.id}/merge`,
                     existing ? { target_id: existing.id } : { target_name: name }, 'POST');
          setMergeTarget(prev => { const n = { ...prev }; delete n[blend.id]; return n; });
        });
      }
    });
  };

  return (
    <>
      <Modal
        isOpen
        onClose={busy ? () => {} : onClose}
        title="Organise customer blends"
        className="customer-blend-organiser"
        size="xl"
        footer={
          <div className="buttons slideout">
            <div>
              <button data-read-safe className="btn-round btn-danger" onClick={onClose} disabled={busy}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                <span className="btn-label">Cancel</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout btn-slideout-primary"
                      onClick={handleSave} disabled={busy}>
                <div className="icon-wrapper">
                  <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>
                    {busy ? 'sync' : 'save'}
                  </span>
                </div>
                <span className="btn-label">
                  {busy ? 'Saving...' : `Save changes${started.length ? ` (${started.length})` : ''}`}
                </span>
              </button>
            </div>
          </div>
        }
      >
        {(error || accounts.error) && <p className="cbo-error">{error || accounts.error}</p>}
        <OrganiserBody
          loading={loading}
          accounts={accounts}
          blends={sortedBlends}
          unassigned={visibleUnassigned}
          catchAll={catchAll}
          search={search}
          setSearch={setSearch}
          selected={selected}
          toggleSelect={toggleSelect}
          toggleSelectAll={toggleSelectAll}
          allSelected={allSelected}
          bulkTarget={bulkTarget}
          setBulkTarget={setBulkTarget}
          applyBulk={applyBulk}
          rowTarget={rowTarget}
          setRowTarget={setRowTarget}
          applyRow={applyRow}
          onViewCustomer={setViewing}
          onMerge={handleMerge}
          mergeTarget={mergeTarget}
          setMergeTarget={setMergeTarget}
          needsLine={needsLine}
          busy={busy}
        />
      </Modal>

      {viewing && (
        <CustomerProductsModal account={viewing} onClose={() => setViewing(null)} />
      )}

      <ConfirmDialog
        isOpen={!!confirm}
        message={confirm?.message || ''}
        onConfirm={confirm?.onConfirm}
        onCancel={() => setConfirm(null)}
      />

      {/* Two real choices rather than a confirm and a dead end: cancel here
          means "take me back to fill them in", which is the more likely answer,
          so it is the one that keeps the user's work in front of them. */}
      <ConfirmDialog
        isOpen={!!savePrompt}
        type="warning"
        title="Some rows have no blend line"
        message={savePrompt?.message || ''}
        confirmText={'File them under "Blend"'}
        cancelText="Let me fill them in"
        onConfirm={savePrompt?.onUseBlend}
        onCancel={savePrompt?.onFillIn}
      />
    </>
  );
}

export default CustomerBlendOrganiser;
